import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  addDays, iso, parseIso, rangeDays, startOfWeek, fmtShort, JOURS_LONG, MOIS, fmtFull,
} from "@/lib/date";
import { ChevronLeft, ChevronRight, Send, Plus, Trash2, Pencil, BadgeCheck, AlertTriangle, XCircle, Ban, Download, CalendarDays, LayoutGrid, RotateCcw, FileSpreadsheet, FileText } from "lucide-react";
import PlanningMonth from "./PlanningMonth";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function Planning() {
  const { user } = useAuth();
  const [viewMode, setViewMode] = useState("week"); // "week" | "month"
  const [monthStart, setMonthStart] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));
  const [shifts, setShifts] = useState([]);
  const [students, setStudents] = useState([]);
  const [avails, setAvails] = useState([]);
  const [holidays, setHolidays] = useState(new Set());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // {student_id, date, start, end, id?}
  const [quotaByStudent, setQuotaByStudent] = useState({}); // {student_id: quota_status}
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelShift, setCancelShift] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [holidayConfirm, setHolidayConfirm] = useState(null); // { holiday_name, message } — pour re-post avec force
  const [showAvails, setShowAvails] = useState(() => {
    const v = localStorage.getItem("pecan_planning_show_avails");
    return v === null ? true : v === "1";
  });
  useEffect(() => {
    localStorage.setItem("pecan_planning_show_avails", showAvails ? "1" : "0");
  }, [showAvails]);

  const weekEnd = useMemo(() => addDays(weekStart, 6), [weekStart]);
  const isAdminLike = user.role === "admin" || user.role === "chef";

  const loadQuotas = async () => {
    if (!isAdminLike) return;
    try {
      const { data } = await api.get("/quota-status", { params: { year: weekStart.getFullYear() } });
      const map = {};
      data.forEach((q) => { map[q.student_id] = q; });
      setQuotaByStudent(map);
    } catch (e) { console.warn("quota-status load failed:", e); }
  };

  const load = async () => {
    const start = iso(weekStart);
    const end = iso(weekEnd);
    const [sh, us] = await Promise.all([
      api.get("/shifts", { params: { start, end } }),
      isAdminLike ? api.get("/users") : Promise.resolve({ data: [] }),
    ]);
    setShifts(sh.data);
    if (isAdminLike) setStudents(us.data.filter((u) => u.role === "student" && u.active));
    if (isAdminLike) {
      const av = await api.get("/availabilities", { params: { start, end } });
      setAvails(av.data);
    }
    const year = weekStart.getFullYear();
    const h = await api.get(`/holidays/${year}`);
    setHolidays(new Set(h.data.map((x) => x.date)));
    await loadQuotas();
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [weekStart]);

  const days = useMemo(() => rangeDays(weekStart, weekEnd), [weekStart, weekEnd]);

  const shiftsByCell = useMemo(() => {
    const map = {};
    // En cas de doublons DB (2 shifts sur student|date), on garde le plus récent
    // (created_at le plus grand) — cohérent avec la logique UPDATE côté backend.
    shifts.forEach((s) => {
      const k = `${s.student_id}|${s.date}`;
      const prev = map[k];
      if (!prev) { map[k] = s; return; }
      const a = s.created_at || "";
      const b = prev.created_at || "";
      if (a >= b) map[k] = s;
    });
    return map;
  }, [shifts]);

  const availsByCell = useMemo(() => {
    const map = {};
    avails.forEach((a) => {
      const k = `${a.user_id}|${a.date}`;
      map[k] = a;
    });
    return map;
  }, [avails]);

  const studentList = isAdminLike ? students : [{ id: user.id, name: user.name }];
  const studentMap = useMemo(() => {
    const m = {};
    students.forEach((s) => { m[s.id] = s; });
    return m;
  }, [students]);

  const openEdit = (student_id, dateIso, existing) => {
    if (!isAdminLike) return;
    const av = availsByCell[`${student_id}|${dateIso}`];
    setEditing({
      id: existing?.id,
      student_id,
      date: dateIso,
      start: existing?.start || av?.start || "08:30",
      end: existing?.end || av?.end || "17:00",
      note: existing?.note || "",
      published: existing?.published,
    });
    setDialogOpen(true);
  };

  const saveShift = async (opts = {}) => {
    try {
      let resp;
      if (editing.id) {
        resp = await api.patch(`/shifts/${editing.id}`, {
          start: editing.start, end: editing.end, note: editing.note,
        });
      } else {
        resp = await api.post("/shifts", {
          student_id: editing.student_id, date: editing.date,
          start: editing.start, end: editing.end, note: editing.note,
          force_holiday: !!opts.force_holiday,
        });
      }
      const qs = resp?.data?.quota_status;
      if (qs?.over) {
        toast.warning(
          `⚠️ Quota dépassé pour ${qs.student_name} : ${qs.projected_hours} h prévues / ${qs.quota_year_hours} h (+${qs.over_hours} h)`,
          { duration: 7000, id: `quota-over-${qs.student_id}` }
        );
      } else if (qs?.near_limit) {
        toast.message(
          `⚠️ ${qs.student_name} approche du quota : ${qs.projected_hours} h / ${qs.quota_year_hours} h (${qs.percent}%)`,
          { duration: 5000, id: `quota-near-${qs.student_id}` }
        );
      } else {
        toast.success("Shift enregistré");
      }
      setHolidayConfirm(null);
      setDialogOpen(false);
      load();
    } catch (e) {
      // Confirmation jour férié : réafficher un dialog dédié
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.code === "holiday_confirmation_required") {
        setHolidayConfirm({
          holiday_name: detail.holiday_name || "Jour férié",
          message: detail.message || "Ce jour est férié. Confirmer ?",
        });
        return;
      }
      toast.error(formatApiError(e));
    }
  };

  const deleteShift = async () => {
    if (!editing?.id) return;
    try {
      await api.delete(`/shifts/${editing.id}`);
      toast.success("Shift supprimé");
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const [publishOpen, setPublishOpen] = useState(false);

  const publishWeek = async () => {
    try {
      const { data } = await api.post("/shifts/publish", { start: iso(weekStart), end: iso(weekEnd) });
      toast.success(`${data.published} shift(s) publié(s) · ${data.emails_to_students || 0} email(s) envoyé(s)`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPublishOpen(false);
    }
  };

  // Calcul du récap pour la confirmation de publication
  const publishPreview = useMemo(() => {
    const drafts = shifts.filter((s) => !s.published && !s.cancelled);
    const byStudent = {};
    drafts.forEach((s) => {
      const u = studentMap[s.student_id];
      const name = u ? (u.first_name || (u.name || "").split(" ")[0] || u.name) : "?";
      byStudent[name] = (byStudent[name] || 0) + 1;
    });
    return { total: drafts.length, byStudent };
  }, [shifts, studentMap]);

  const hoursUntil = (shift) => {
    const [y, m, d] = shift.date.split("-").map(Number);
    const [hh, mm] = (shift.start || "00:00").split(":").map(Number);
    return (new Date(y, m - 1, d, hh, mm).getTime() - Date.now()) / 36e5;
  };

  const openCancelStudent = (shift) => {
    if (shift.cancelled) return;
    const left = hoursUntil(shift);
    if (left < 48) {
      toast.error("Annulation impossible à moins de 48h du début du shift. Merci de contacter le chef d'atelier.", { duration: 7000 });
      return;
    }
    setCancelShift(shift);
    setCancelReason("");
    setCancelOpen(true);
  };

  const submitCancel = async () => {
    try {
      const { data } = await api.post(
        `/shifts/${cancelShift.id}/cancel?force=true`,
        { reason: cancelReason || null }
      );
      const n = (data?.replacements || []).length;
      toast.success(
        n
          ? `Shift annulé. ${n} remplaçant(s) potentiel(s) — admin et chef ont été notifiés.`
          : "Shift annulé. Admin et chef ont été notifiés (aucun remplaçant disponible)."
      );
      setCancelOpen(false);
      setCancelShift(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const weekHoursByStudent = useMemo(() => {
    const map = {};
    const activeIds = new Set(studentList.map((s) => s.id));
    const wkStart = iso(weekStart);
    const wkEnd = iso(weekEnd);
    // Itère UNIQUEMENT sur shiftsByCell (1 shift/student/date) pour rester cohérent
    // avec ce qui est affiché dans les cellules. Évite les doublons DB.
    Object.values(shiftsByCell).forEach((s) => {
      if (!s || s.cancelled) return;
      if (!s.date || s.date < wkStart || s.date > wkEnd) return;
      if (!activeIds.has(s.student_id)) return;
      const [h1, m1] = (s.start || "0:0").split(":").map(Number);
      const [h2, m2] = (s.end || "0:0").split(":").map(Number);
      const mins = Math.max(0, ((h2 || 0) * 60 + (m2 || 0)) - ((h1 || 0) * 60 + (m1 || 0)));
      map[s.student_id] = (map[s.student_id] || 0) + mins;
    });
    return map;
  }, [shiftsByCell, studentList, weekStart, weekEnd]);

  const fmtHoursShort = (mins) => {
    if (!mins) return "";
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m ? `${h}h${String(m).padStart(2, "0")}` : `${h}h`;
  };

  const exportWeekCSV = () => {
    const rows = [];
    rows.push(["Étudiant", ...days.map((d) => fmtShort(d)), "Total"].join(";"));
    students.forEach((st) => {
      const cells = days.map((d) => {
        const s = shiftsByCell[`${st.id}|${iso(d)}`];
        if (!s || s.cancelled) return "";
        return `${s.start} → ${s.end}${s.published ? "" : " (brouillon)"}`;
      });
      const total = fmtHoursShort(weekHoursByStudent[st.id] || 0);
      rows.push([st.name, ...cells, total].join(";"));
    });
    const csv = "\uFEFF" + rows.join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `planning-semaine-${iso(weekStart)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success("Planning exporté en Excel");
  };

  const exportWeekPDF = async () => {
    try {
      const resp = await api.get(`/shifts/export-week-pdf?start=${iso(weekStart)}`, {
        responseType: "blob",
      });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `planning-semaine-${iso(weekStart)}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success("Planning exporté en PDF");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="page-planning">
      {isAdminLike && (
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-full bg-[#F1EDE3] p-1 border border-[#DDE7E4]" data-testid="planning-view-toggle">
            <button
              type="button"
              onClick={() => setViewMode("week")}
              data-testid="view-week"
              className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all ${
                viewMode === "week" ? "bg-[#2C4C3B] text-white shadow-sm" : "text-[#3D504B] hover:bg-[#E4EBE1]"
              }`}
            >
              <CalendarDays className="w-3.5 h-3.5" /> Semaine
            </button>
            <button
              type="button"
              onClick={() => setViewMode("month")}
              data-testid="view-month"
              className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all ${
                viewMode === "month" ? "bg-[#2C4C3B] text-white shadow-sm" : "text-[#3D504B] hover:bg-[#E4EBE1]"
              }`}
            >
              <LayoutGrid className="w-3.5 h-3.5" /> Mois
            </button>
          </div>
          {(viewMode === "week" || viewMode === "month") && (
            <button
              type="button"
              onClick={() => setShowAvails((v) => !v)}
              data-testid="toggle-show-avails"
              title={showAvails ? "Masquer les disponibilités" : "Afficher les disponibilités"}
              className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-semibold border transition-all ${
                showAvails
                  ? "bg-[#E4EBE1] border-[#2C4C3B] text-[#2C4C3B]"
                  : "bg-white border-[#DDE7E4] text-[#6b7f79] hover:border-[#2C4C3B] hover:text-[#2C4C3B]"
              }`}
            >
              <span className={`w-2 h-2 rounded-full ${showAvails ? "bg-[#2C4C3B]" : "bg-[#B8C7C2]"}`} />
              {showAvails ? "Dispos affichées" : "Voir les dispos"}
            </button>
          )}
        </div>
      )}

      {viewMode === "month" && isAdminLike ? (
        <PlanningMonth
          monthStart={monthStart}
          setMonthStart={setMonthStart}
          showAvails={showAvails}
          onSelectDay={(d) => {
            setWeekStart(startOfWeek(d));
            setViewMode("week");
          }}
        />
      ) : (
      <>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="label-tiny">Planning équipe</p>
          <h1 className="font-display text-3xl sm:text-4xl text-[#1A2B27] mt-1">
            Semaine du {fmtShort(weekStart)}
          </h1>
          <p className="text-[#6b7f79] mt-1">{fmtShort(weekStart)} → {fmtShort(weekEnd)}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline" size="icon"
            onClick={() => setWeekStart(addDays(weekStart, -7))}
            data-testid="week-prev"
            className="rounded-full border-[#DDE7E4]"
          ><ChevronLeft className="w-4 h-4" /></Button>
          <Button
            variant="outline" size="sm"
            onClick={() => setWeekStart(startOfWeek(new Date()))}
            data-testid="week-today"
            className="rounded-full border-[#DDE7E4] text-[#6b7f79]"
          >Aujourd'hui</Button>
          <Button
            variant="outline" size="icon"
            onClick={() => setWeekStart(addDays(weekStart, 7))}
            data-testid="week-next"
            className="rounded-full border-[#DDE7E4]"
          ><ChevronRight className="w-4 h-4" /></Button>
          {isAdminLike && (
            <>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    data-testid="export-week-button"
                    className="rounded-full border-[#2C4C3B] text-[#2C4C3B] hover:bg-[#E4EBE1]"
                  >
                    <Download className="w-4 h-4 mr-2" /> Exporter
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-white border-[#DDE7E4] shadow-xl">
                  <DropdownMenuItem
                    onClick={exportWeekCSV}
                    data-testid="export-week-excel"
                    className="cursor-pointer"
                  >
                    <FileSpreadsheet className="w-4 h-4 mr-2 text-[#2C4C3B]" />
                    <div className="flex flex-col">
                      <span className="font-semibold text-sm">Excel (.csv)</span>
                      <span className="text-[11px] text-[#94ADA7]">Tableau pour tableur</span>
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={exportWeekPDF}
                    data-testid="export-week-pdf"
                    className="cursor-pointer"
                  >
                    <FileText className="w-4 h-4 mr-2 text-[#2C4C3B]" />
                    <div className="flex flex-col">
                      <span className="font-semibold text-sm">PDF à imprimer</span>
                      <span className="text-[11px] text-[#94ADA7]">Mise en page atelier</span>
                    </div>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button
                onClick={() => setPublishOpen(true)}
                data-testid="publish-week-button"
                className="rounded-full bg-[#2C4C3B] hover:bg-[#1F3528] shadow-md hover:-translate-y-0.5 transition-all"
              >
                <Send className="w-4 h-4 mr-2" /> Publier la semaine
              </Button>
            </>
          )}
        </div>
      </div>

      {isAdminLike && Object.values(quotaByStudent).some((q) => q.over || q.near_limit) && (
        <Card className="card-soft border-0 bg-[#E4EBE1] border border-[#5C7F76]" data-testid="quota-alert-banner">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-[#2C4C3B] mt-0.5 shrink-0" />
              <div className="flex-1">
                <div className="font-semibold text-[#1A2B27]">
                  Alerte quota annuel {weekStart.getFullYear()}
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {Object.values(quotaByStudent)
                    .filter((q) => q.over || q.near_limit)
                    .map((q) => (
                      <Badge
                        key={q.student_id}
                        className={`rounded-full px-3 py-1 ${q.over ? "bg-[#E53935] hover:bg-[#E53935] text-white" : "bg-[#FFB300] hover:bg-[#FFB300] text-white"}`}
                        data-testid={`quota-alert-${q.student_id}`}
                      >
                        {q.student_name} : {q.projected_hours} h / {q.quota_year_hours} h ({q.percent}%)
                        {q.over && <span className="ml-1 font-bold">— +{q.over_hours} h</span>}
                      </Badge>
                    ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="card-soft border-0 overflow-hidden">
        <CardContent className="p-0 overflow-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-[#F1EDE3]">
              <tr>
                <th className="sticky left-0 z-10 bg-[#F1EDE3] text-left p-3 font-semibold text-[#1A2B27] min-w-[140px]">Étudiant</th>
                {days.map((d) => {
                  const isSunday = d.getDay() === 0;
                  const isHoliday = holidays.has(iso(d));
                  const dim = isSunday || isHoliday;
                  return (
                    <th
                      key={iso(d)}
                      className={`p-2 text-center font-semibold border-l border-[#DDE7E4] min-w-[120px] ${dim ? "bg-[#F1EDE3] text-[#B8C7C2]" : "text-[#1A2B27]"}`}
                    >
                      <div className="text-[10px] uppercase tracking-wider">{JOURS_LONG[(d.getDay() + 6) % 7].slice(0, 3)}</div>
                      <div className="font-display text-base">{d.getDate()}</div>
                      <div className="text-[10px] text-[#94ADA7]">{MOIS[d.getMonth()].slice(0, 3).toLowerCase()}</div>
                      {isHoliday && !isSunday && (
                        <div className="text-[9px] text-[#FF9800] font-semibold uppercase mt-0.5">Férié</div>
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {studentList.map((st) => {
                const qs = quotaByStudent[st.id];
                return (
                <tr key={st.id} className="border-t border-[#DDE7E4]">
                  <td className="sticky left-0 bg-white z-10 p-3 font-medium text-[#1A2B27]">
                    <div className="flex items-center gap-1.5">
                      <span>{st.name}</span>
                      {weekHoursByStudent[st.id] > 0 && (
                        <span
                          data-testid={`week-hours-${st.id}`}
                          title={`Heures planifiées cette semaine`}
                          className="inline-flex items-center bg-[#E4EBE1] text-[#2C4C3B] text-[10px] font-semibold px-1.5 py-0.5 rounded-full border border-[#C9DBD6]"
                        >
                          {fmtHoursShort(weekHoursByStudent[st.id])}
                        </span>
                      )}
                      {qs?.over && (
                        <span
                          title={`Quota dépassé : ${qs.projected_hours}h / ${qs.quota_year_hours}h`}
                          data-testid={`quota-row-badge-${st.id}`}
                          className="inline-flex items-center gap-0.5 bg-[#E53935] text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full"
                        >
                          <AlertTriangle className="w-2.5 h-2.5" /> {qs.projected_hours}h
                        </span>
                      )}
                      {!qs?.over && qs?.near_limit && (
                        <span
                          title={`Approche du quota : ${qs.percent}%`}
                          data-testid={`quota-row-badge-${st.id}`}
                          className="inline-flex items-center gap-0.5 bg-[#FFB300] text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full"
                        >
                          {qs.percent}%
                        </span>
                      )}
                    </div>
                  </td>
                  {days.map((d) => {
                    const isoD = iso(d);
                    const isSunday = d.getDay() === 0;
                    const isHoliday = holidays.has(isoD);
                    const isClosed = isSunday; // Fermé strict = dimanche uniquement (fériés éditables)
                    const shift = shiftsByCell[`${st.id}|${isoD}`];
                    const av = availsByCell[`${st.id}|${isoD}`];
                    // Teinte de fond dispos (uniquement admin/chef, toggle activé, pas de shift déjà créé)
                    let availTintCls = "";
                    if (showAvails && isAdminLike && !shift && !isClosed) {
                      if (av?.available) availTintCls = "bg-[#E8F5E9]"; // vert clair
                      else if (av && av.available === false) availTintCls = "bg-[#FFEBEE]"; // rouge clair
                      else availTintCls = "bg-[#FAFAFA]"; // gris — pas soumis
                    }
                    return (
                      <td
                        key={isoD}
                        className={`align-top p-1.5 border-l border-[#DDE7E4] ${isClosed ? "bg-[#F7F9F8]" : isHoliday ? "bg-[#FFF8E1]" : availTintCls}`}
                      >
                        {isClosed ? (
                          <div className="text-[10px] text-[#B8C7C2] text-center italic py-2">Fermé</div>
                        ) : shift ? (
                          shift.cancelled ? (
                            <div
                              data-testid={`shift-cancelled-${st.id}-${isoD}`}
                              className="w-full rounded-xl px-2.5 py-2 bg-[#FFEBEE] border border-[#E53935] text-left"
                            >
                              <div className="text-xs font-bold text-[#C62828] line-through">{shift.start} → {shift.end}</div>
                              <div className="flex items-center gap-0.5 text-[9px] text-[#C62828] font-semibold mt-0.5 uppercase tracking-wider">
                                <Ban className="w-3 h-3" /> Annulé
                              </div>
                              {shift.cancel_reason && (
                                <div className="text-[9px] text-[#94ADA7] italic mt-0.5 truncate" title={shift.cancel_reason}>
                                  {shift.cancel_reason}
                                </div>
                              )}
                              {isAdminLike && (
                                <button
                                  type="button"
                                  onClick={async () => {
                                    if (!window.confirm(`Restaurer ce shift (${shift.start} → ${shift.end}) ?`)) return;
                                    try {
                                      await api.post(`/shifts/${shift.id}/uncancel`);
                                      toast.success("Shift restauré");
                                      load();
                                    } catch (e) { toast.error(formatApiError(e)); }
                                  }}
                                  data-testid={`shift-uncancel-${shift.id}`}
                                  className="mt-1 w-full flex items-center justify-center gap-1 text-[10px] font-semibold text-[#2C4C3B] bg-white border border-[#2C4C3B] rounded-full py-1 hover:bg-[#E4EBE1]"
                                >
                                  <RotateCcw className="w-3 h-3" /> Restaurer
                                </button>
                              )}
                            </div>
                          ) : (
                          <button
                            type="button"
                            disabled={!isAdminLike && shift.student_id !== user.id}
                            onClick={() => {
                              if (isAdminLike) openEdit(st.id, isoD, shift);
                              else if (shift.student_id === user.id && shift.published) openCancelStudent(shift);
                            }}
                            data-testid={`shift-cell-${st.id}-${isoD}`}
                            className={`w-full text-left rounded-xl px-2.5 py-2 transition-all hover:shadow ${shift.published ? "bg-[#C9DBD6] border border-[#2C4C3B]" : "bg-[#E4EBE1] border border-dashed border-[#2C4C3B]"}`}
                          >
                            <div className="text-xs font-bold text-[#1A2B27]">{shift.start} → {shift.end}</div>
                            {shift.published && (
                              <div className="flex items-center gap-0.5 text-[9px] text-[#3C6750] font-semibold mt-0.5">
                                <BadgeCheck className="w-3 h-3" /> Publié
                              </div>
                            )}
                            {!shift.published && (
                              <div className="text-[9px] text-[#3C6750] mt-0.5 font-semibold">Brouillon</div>
                            )}
                            {!isAdminLike && shift.student_id === user.id && shift.published && (
                              <div className="text-[9px] text-[#3C6750] mt-1 font-semibold flex items-center gap-0.5">
                                <XCircle className="w-3 h-3" /> Annuler
                              </div>
                            )}
                          </button>
                          )
                        ) : isAdminLike ? (
                          <button
                            type="button"
                            onClick={() => openEdit(st.id, isoD)}
                            data-testid={`shift-empty-${st.id}-${isoD}`}
                            className={`w-full rounded-xl px-2 py-2 border border-dashed hover:bg-[#FFFFFF] hover:border-[#2C4C3B] transition-all text-left ${
                              showAvails && av?.available ? "border-[#4CAF50]" :
                              showAvails && av && !av.available ? "border-[#E57373]" :
                              "border-[#DDE7E4]"
                            }`}
                          >
                            {av?.available ? (
                              <>
                                <div className="text-[10px] text-[#2E7D32] font-semibold uppercase">Dispo</div>
                                <div className="text-xs text-[#388E3C]">{(av.start || "").slice(0, 5)}-{(av.end || "").slice(0, 5)}</div>
                              </>
                            ) : av && !av.available ? (
                              <div className="text-[10px] text-[#C62828] font-semibold uppercase">Non dispo</div>
                            ) : showAvails ? (
                              <div className="text-[10px] text-[#B8C7C2] text-center italic">pas soumis</div>
                            ) : (
                              <div className="text-[10px] text-[#B8C7C2] text-center"><Plus className="w-3 h-3 inline" /></div>
                            )}
                          </button>
                        ) : (
                          <div className="text-[10px] text-[#B8C7C2] text-center py-2">—</div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
              })}
            </tbody>
            {studentList.length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-[#2C4C3B] bg-[#F1EDE3]">
                  <td className="sticky left-0 bg-[#F1EDE3] z-10 p-3 font-semibold text-[#1A2B27]">
                    Total équipe
                  </td>
                  {days.map((d) => {
                    const isoD = iso(d);
                    let mins = 0;
                    const activeIds = new Set(studentList.map((s) => s.id));
                    // Itère sur shiftsByCell (1 shift/student/date) pour rester cohérent avec l'affichage
                    Object.values(shiftsByCell).forEach((s) => {
                      if (!s || s.cancelled) return;
                      if (s.date !== isoD) return;
                      if (!activeIds.has(s.student_id)) return;
                      const [h1, m1] = (s.start || "0:0").split(":").map(Number);
                      const [h2, m2] = (s.end || "0:0").split(":").map(Number);
                      mins += Math.max(0, ((h2 || 0) * 60 + (m2 || 0)) - ((h1 || 0) * 60 + (m1 || 0)));
                    });
                    return (
                      <td key={isoD} className="p-2 text-center border-l border-[#DDE7E4] text-[#2C4C3B] font-semibold text-xs" data-testid={`week-day-total-${isoD}`}>
                        {mins > 0 ? fmtHoursShort(mins) : "—"}
                      </td>
                    );
                  })}
                </tr>
              </tfoot>
            )}
          </table>
        </CardContent>
      </Card>

      {/* Total global de la semaine */}
      {isAdminLike && (
        <Card className="card-soft border-0" data-testid="week-grand-total">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="text-sm text-[#6b7f79]">
              Total planifié cette semaine — tous étudiants confondus
            </div>
            <div className="text-2xl font-display text-[#2C4C3B]" data-testid="week-grand-total-value">
              {fmtHoursShort(Object.values(weekHoursByStudent).reduce((a, b) => a + b, 0)) || "0h"}
            </div>
          </CardContent>
        </Card>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="shift-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#1A2B27]">
              {editing?.id ? "Modifier le shift" : "Ajouter un shift"}
            </DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <p className="text-sm text-[#6b7f79]">
                {students.find((s) => s.id === editing.student_id)?.name || ""} — {fmtFull(parseIso(editing.date))}
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label-tiny">Arrivée</label>
                  <Input
                    type="time"
                    value={editing.start}
                    onChange={(e) => setEditing({ ...editing, start: e.target.value })}
                    data-testid="shift-edit-start"
                    className="mt-1 h-11 rounded-xl border-[#DDE7E4]"
                  />
                </div>
                <div>
                  <label className="label-tiny">Départ</label>
                  <Input
                    type="time"
                    value={editing.end}
                    onChange={(e) => setEditing({ ...editing, end: e.target.value })}
                    data-testid="shift-edit-end"
                    className="mt-1 h-11 rounded-xl border-[#DDE7E4]"
                  />
                </div>
              </div>
              <div>
                <label className="label-tiny">Note (optionnel)</label>
                <Input
                  value={editing.note}
                  onChange={(e) => setEditing({ ...editing, note: e.target.value })}
                  placeholder="Préparation tarte, livraison…"
                  className="mt-1 h-11 rounded-xl border-[#DDE7E4]"
                  data-testid="shift-edit-note"
                />
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            {editing?.id && (
              <Button
                variant="ghost"
                onClick={deleteShift}
                data-testid="shift-delete-button"
                className="text-[#3C6750] hover:text-[#3C6750] hover:bg-[#FFEBEE]"
              >
                <Trash2 className="w-4 h-4 mr-1" /> Supprimer
              </Button>
            )}
            <Button
              onClick={() => saveShift()}
              data-testid="shift-save-button"
              className="rounded-full bg-[#2C4C3B] hover:bg-[#1F3528]"
            >
              {editing?.id ? <><Pencil className="w-4 h-4 mr-1" /> Mettre à jour</> : <><Plus className="w-4 h-4 mr-1" /> Créer</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation jour férié */}
      <Dialog open={!!holidayConfirm} onOpenChange={(o) => !o && setHolidayConfirm(null)}>
        <DialogContent data-testid="shift-holiday-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#1A2B27]">Attention, jour férié</DialogTitle>
          </DialogHeader>
          {holidayConfirm && (
            <div className="space-y-3 text-sm">
              <div className="p-3 bg-[#FFF4E5] rounded-xl border border-[#FFB300] text-[#8B5A00]">
                <div className="font-semibold text-[#1A2B27]">{holidayConfirm.holiday_name}</div>
                <div className="mt-1">{holidayConfirm.message}</div>
              </div>
              <p className="text-[#6b7f79]">Souhaites-tu vraiment programmer un shift ce jour-là ?</p>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button
              variant="ghost"
              onClick={() => setHolidayConfirm(null)}
              data-testid="shift-holiday-cancel"
              className="text-[#6b7f79]"
            >
              Annuler
            </Button>
            <Button
              onClick={() => saveShift({ force_holiday: true })}
              data-testid="shift-holiday-confirm"
              className="rounded-full bg-[#FF9800] hover:bg-[#F57C00] text-white"
            >
              Créer quand même
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent data-testid="shift-cancel-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#1A2B27]">Annuler ce shift ?</DialogTitle>
          </DialogHeader>
          {cancelShift && (
            <div className="space-y-3 text-sm">
              <p className="text-[#6b7f79]">
                {fmtFull(parseIso(cancelShift.date))}<br />
                <span className="font-semibold text-[#1A2B27]">{cancelShift.start} → {cancelShift.end}</span>
              </p>
              <div className="p-3 bg-[#E4EBE1] rounded-xl border border-[#5C7F76] text-[#6b7f79] text-xs">
                L'admin et le chef d'atelier seront prévenus immédiatement avec la liste des étudiants disponibles ce jour-là.
              </div>
              <div>
                <label className="label-tiny">Motif (optionnel)</label>
                <Input
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="Ex. examen, maladie…"
                  data-testid="shift-cancel-reason"
                  className="mt-1 h-11 rounded-xl border-[#DDE7E4]"
                />
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button
              variant="ghost"
              onClick={() => setCancelOpen(false)}
              data-testid="shift-cancel-back"
              className="text-[#6b7f79]"
            >
              Garder le shift
            </Button>
            <Button
              onClick={submitCancel}
              data-testid="shift-cancel-confirm"
              className="rounded-full bg-[#C62828] hover:bg-[#A01F1F] text-white"
            >
              <Ban className="w-4 h-4 mr-1" /> Confirmer l'annulation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </>
      )}

      {/* Confirmation de publication de la semaine */}
      <AlertDialog open={publishOpen} onOpenChange={setPublishOpen}>
        <AlertDialogContent data-testid="publish-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl text-[#1A2B27]">
              Publier la semaine du {fmtShort(weekStart)} ?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-sm text-[#6b7f79] space-y-3 mt-2">
                {publishPreview.total === 0 ? (
                  <p className="text-[#6b7f79]">
                    Tous les shifts de cette semaine sont déjà publiés — rien à envoyer.
                  </p>
                ) : (
                  <>
                    <p>
                      <b className="text-[#1A2B27]">{publishPreview.total} shift{publishPreview.total > 1 ? "s" : ""} brouillon</b> {publishPreview.total > 1 ? "seront publiés" : "sera publié"} et un email de notification sera envoyé aux étudiants concernés.
                    </p>
                    <div className="bg-[#F1EDE3] rounded-xl p-3 max-h-40 overflow-y-auto">
                      <div className="text-[10px] uppercase tracking-wider text-[#2C4C3B] font-semibold mb-1.5">Récap par étudiant</div>
                      <ul className="space-y-0.5" data-testid="publish-confirm-recap">
                        {Object.entries(publishPreview.byStudent).sort().map(([name, count]) => (
                          <li key={name} className="flex justify-between text-sm text-[#1A2B27]">
                            <span>{name}</span>
                            <span className="font-mono text-[#2C4C3B]">{count} shift{count > 1 ? "s" : ""}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <p className="text-xs italic text-[#94ADA7]">
                      La publication est manuelle : les shifts que tu ajouteras après resteront en brouillon jusqu'à ta prochaine publication.
                    </p>
                  </>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="publish-confirm-cancel" className="rounded-full">
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={publishWeek}
              disabled={publishPreview.total === 0}
              data-testid="publish-confirm-ok"
              className="rounded-full bg-[#2C4C3B] hover:bg-[#1F3528]"
            >
              <Send className="w-4 h-4 mr-1.5" /> Confirmer la publication
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
